import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { getEffectiveFieldMode, useFieldMode } from '../../auth/FieldModeContext';
import { useWorkspaceBranding } from '../../branding/BrandingContext';
import { getNavLinks, isMobileNavActive, isTechnicianShellActive } from './navLinks';
import './app-shell.css';

type SliderRect = {
    left: number;
    width: number;
    top: number;
    height: number;
};

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

function smoothstep(t: number): number {
    const x = Math.min(1, Math.max(0, t));
    return x * x * (3 - 2 * x);
}

function measureLinkRects(links: HTMLElement[], nav: HTMLElement): SliderRect[] {
    const navRect = nav.getBoundingClientRect();
    return links.map((el) => {
        const r = el.getBoundingClientRect();
        return {
            left: r.left - navRect.left + nav.scrollLeft,
            width: r.width,
            top: r.top - navRect.top,
            height: r.height
        };
    });
}

/**
 * Continuous pill along the nav: morphs between neighboring buttons as the
 * pointer travels from one center to the next (so the 6px gaps don't jerk).
 * Midway, the pill spans half of each button plus the space between.
 */
function sliderFromPointer(links: HTMLElement[], nav: HTMLElement, clientX: number): SliderRect | null {
    if (!links.length) return null;

    const navRect = nav.getBoundingClientRect();
    const x = clientX - navRect.left + nav.scrollLeft;
    const rects = measureLinkRects(links, nav);

    if (rects.length === 1) return rects[0];

    const centers = rects.map((r) => r.left + r.width / 2);

    if (x <= centers[0]) return rects[0];
    if (x >= centers[centers.length - 1]) return rects[rects.length - 1];

    for (let i = 0; i < centers.length - 1; i++) {
        const c0 = centers[i];
        const c1 = centers[i + 1];
        if (x < c0 || x > c1) continue;

        const t = smoothstep((x - c0) / (c1 - c0));
        const a = rects[i];
        const b = rects[i + 1];
        const gap = Math.max(0, b.left - (a.left + a.width));
        const midLeft = a.left + a.width * 0.5;
        const midWidth = a.width * 0.5 + gap + b.width * 0.5;

        if (t <= 0.5) {
            const u = t * 2;
            return {
                left: lerp(a.left, midLeft, u),
                width: lerp(a.width, midWidth, u),
                top: lerp(a.top, b.top, t),
                height: lerp(a.height, b.height, t)
            };
        }

        const u = (t - 0.5) * 2;
        return {
            left: lerp(midLeft, b.left, u),
            width: lerp(midWidth, b.width, u),
            top: lerp(a.top, b.top, t),
            height: lerp(a.height, b.height, t)
        };
    }

    return rects[0];
}

function sliderFromActive(nav: HTMLElement): SliderRect | null {
    const active = nav.querySelector<HTMLElement>('.app-nav-link-active');
    if (!active) return null;
    const navRect = nav.getBoundingClientRect();
    const r = active.getBoundingClientRect();
    return {
        left: r.left - navRect.left + nav.scrollLeft,
        width: r.width,
        top: r.top - navRect.top,
        height: r.height
    };
}

function applySliderStyle(el: HTMLElement, rect: SliderRect) {
    el.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0)`;
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
}

const HOVER_SMOOTH = 0.22;
const SETTLE_SMOOTH = 0.14;

function TopNav(): React.JSX.Element {
    const { pathname } = useLocation();
    const { user, logout } = useAuth();
    const { fieldMode } = useFieldMode();
    const { appName } = useWorkspaceBranding();
    const navigate = useNavigate();
    const navRef = useRef<HTMLElement>(null);
    const sliderRef = useRef<HTMLSpanElement>(null);
    const currentRef = useRef<SliderRect | null>(null);
    const targetRef = useRef<SliderRect | null>(null);
    const hoveringRef = useRef(false);
    const rafRef = useRef(0);
    const [pointerInside, setPointerInside] = useState(false);
    const [sliderReady, setSliderReady] = useState(false);

    const effectiveFieldMode = getEffectiveFieldMode(fieldMode);
    const techShell = isTechnicianShellActive(user?.role, effectiveFieldMode);
    const navItems = getNavLinks(user?.role, effectiveFieldMode);
    const navKey = navItems.map((item) => item.to).join('|');
    const homePath = techShell ? '/technician/dashboard' : '/admin/dashboard';

    const stopRaf = () => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = 0;
        }
    };

    const tick = () => {
        const el = sliderRef.current;
        const target = targetRef.current;
        if (!el || !target) {
            rafRef.current = 0;
            return;
        }

        const smooth = hoveringRef.current ? HOVER_SMOOTH : SETTLE_SMOOTH;
        const current = currentRef.current;
        if (!current) {
            currentRef.current = { ...target };
            applySliderStyle(el, target);
            setSliderReady(true);
            rafRef.current = requestAnimationFrame(tick);
            return;
        }

        const next: SliderRect = {
            left: lerp(current.left, target.left, smooth),
            width: lerp(current.width, target.width, smooth),
            top: lerp(current.top, target.top, smooth),
            height: lerp(current.height, target.height, smooth)
        };

        const settled =
            Math.abs(next.left - target.left) < 0.2 &&
            Math.abs(next.width - target.width) < 0.2 &&
            Math.abs(next.top - target.top) < 0.2 &&
            Math.abs(next.height - target.height) < 0.2;

        currentRef.current = settled ? { ...target } : next;
        applySliderStyle(el, currentRef.current);

        if (hoveringRef.current || !settled) {
            rafRef.current = requestAnimationFrame(tick);
        } else {
            rafRef.current = 0;
        }
    };

    const ensureTick = () => {
        if (!rafRef.current) {
            rafRef.current = requestAnimationFrame(tick);
        }
    };

    const setTarget = (rect: SliderRect | null, snap = false) => {
        targetRef.current = rect;
        if (!rect) {
            setSliderReady(false);
            currentRef.current = null;
            stopRaf();
            return;
        }
        if (snap) {
            currentRef.current = { ...rect };
            const el = sliderRef.current;
            if (el) applySliderStyle(el, rect);
            setSliderReady(true);
        }
        ensureTick();
    };

    const placeOnActive = (snap = false) => {
        const nav = navRef.current;
        if (!nav) return;
        const rect = sliderFromActive(nav);
        if (rect) setTarget(rect, snap);
    };

    useLayoutEffect(() => {
        if (hoveringRef.current) return;
        placeOnActive(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when route / nav set changes
    }, [pathname, navKey]);

    useEffect(() => {
        const onResize = () => {
            if (hoveringRef.current) return;
            placeOnActive(true);
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            stopRaf();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, navKey]);

    const updateFromPointer = (clientX: number) => {
        const nav = navRef.current;
        if (!nav) return;
        const links = Array.from(nav.querySelectorAll<HTMLElement>('.app-nav-link'));
        const next = sliderFromPointer(links, nav, clientX);
        if (next) setTarget(next);
    };

    const handlePointerEnter = (e: React.PointerEvent<HTMLElement>) => {
        hoveringRef.current = true;
        setPointerInside(true);
        updateFromPointer(e.clientX);
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLElement>) => {
        if (!hoveringRef.current) return;
        updateFromPointer(e.clientX);
    };

    const handlePointerLeave = () => {
        hoveringRef.current = false;
        setPointerInside(false);
        placeOnActive(false);
    };

    return (
        <header className={`app-nav${techShell ? ' app-nav--technician' : ''}`}>
            <div className="app-nav-inner">
                <Link
                    to={homePath}
                    className="app-brand"
                    aria-label="Home"
                    onClick={() => {
                        window.scrollTo(0, 0);
                    }}
                >
                    <img className="app-brand-logo" src="/logo.svg" alt="Logo" />
                    <span className="app-brand-text">{appName}</span>
                </Link>
                <nav
                    ref={navRef}
                    className="app-nav-links app-nav-links--desktop"
                    aria-label="Main"
                    onPointerEnter={handlePointerEnter}
                    onPointerMove={handlePointerMove}
                    onPointerLeave={handlePointerLeave}
                >
                    <span
                        ref={sliderRef}
                        className={`app-nav-slider${sliderReady ? ' is-visible' : ''}${pointerInside ? ' is-hovering' : ''}`}
                        aria-hidden="true"
                    />
                    {navItems.map((item) => (
                        <NavLink
                            key={`${item.to}-${item.label}`}
                            to={item.to}
                            end={item.to.endsWith('/dashboard')}
                            className={
                                isMobileNavActive(pathname, item.to)
                                    ? 'app-nav-link app-nav-link-active'
                                    : 'app-nav-link'
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="app-user app-user--desktop">
                    <span className="app-user-name">{user?.name || 'Admin'}</span>
                    <button
                        type="button"
                        className="app-user-btn"
                        onClick={async () => {
                            await logout();
                            navigate('/login');
                        }}
                    >
                        Sign out
                    </button>
                </div>
                <div className="app-user app-user--mobile-bar">
                    <button
                        type="button"
                        className="app-user-btn app-user-btn--mobile"
                        onClick={async () => {
                            await logout();
                            navigate('/login');
                        }}
                    >
                        Sign out
                    </button>
                </div>
            </div>
        </header>
    );
}

export default TopNav;
